import React, {useState, useRef, useMemo} from 'react';
import {useModal} from 'react-use-modal';
import OdataProvider from './AgGridOdata/index';
import {AgGridReact} from 'ag-grid-react';

import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-material.css'; // Optional theme CSS
import {Button, Container} from '@mui/material';
import {ApiCallArgs} from './AgGridOdata/types';
import {CopyIconRenderer, DeleteIconRenderer} from './AgGridAddons/renderers';
import {getErrorText, handleLoading} from './AgGridAddons/utils';
import {action, CanI} from '../../CanI';

import {showToastAlert} from '../../functions/alerts';
import {inputFieldsForm, okCancelForm, okOnlyForm} from './AgGridAddons/forms';
import {getFieldLimits} from './AgGridOdata/utils';
import {CellClickedEvent} from 'ag-grid-community';

export interface PlAgGridProps {
    makeCall: any;
    addButtonFunc?: string;
    addButtonAction?: action;
    addButtonExtraFunc?: () => boolean;
    addFormHeader?: string;
    addFormFields?: any;
    addToastSuccessMessage?: string;
    createMutation?: any;
    getCreateMutationObject?(input: any): any;
    putMutation?: any;
    getPutMutationObject?(event: any): any;
    getDeleteMessage?(params): string;
    deleteMutation?: any;
    getDeleteMutationObject?(params: any): any;
    deleteToastSuccessMessage?: string;
    onMutationSuccess?: () => void;
    copyFormHeader?: string;
    copyFormFields?: any;
    copyToastSuccessMessage?: string;
    copyMutation?: any;
    getCopyMutationObject?(params: any, data: any): any;
    customButtonText?: string;
    customButtonOnClick?: () => void;
    customButtonFunc?: string;
    customButtonAction?: action;
    customButtonExtraFunc?: () => boolean;
    customFormHeader?: string;
    customFormFields?: any;
    customToastSuccessMessage?: string;
    customCreateMutation?: any;
    customButtonConfirmText?: string;
    getCustomCreateMutationObject?(input: any): any;
    customSuccessCallback?: () => void;
    columns: any;
    getRowId?(params): any;
    canDelete?: boolean;
    canEdit?: boolean;
    canCopy?: boolean;
    wideGrid?: boolean;
    selectionColumnKey?: string;
    selectionColumnValue?: any;
    launcherFn?: (id: string) => void;
    gridKey?: string;
}

//makeCall is a useLazy Query with Odata tagged API endpoint
export const MemoizedPlAgGrid = React.memo(function PlAgGrid({
    makeCall,
    addButtonFunc,
    addButtonAction,
    addButtonExtraFunc,
    addFormHeader,
    addFormFields,
    addToastSuccessMessage,
    createMutation,
    getCreateMutationObject,
    putMutation,
    getPutMutationObject,
    getDeleteMessage,
    deleteMutation,
    getDeleteMutationObject,
    deleteToastSuccessMessage,
    onMutationSuccess,
    copyFormHeader,
    copyFormFields,
    copyToastSuccessMessage,
    copyMutation,
    getCopyMutationObject,
    customButtonText,
    customButtonOnClick,
    customButtonFunc,
    customButtonAction,
    customButtonExtraFunc,
    customFormHeader,
    customFormFields,
    customToastSuccessMessage,
    customCreateMutation,
    customButtonConfirmText,
    getCustomCreateMutationObject,
    customSuccessCallback,
    columns,
    getRowId,
    canDelete,
    canEdit,
    canCopy,
    wideGrid,
    selectionColumnKey,
    selectionColumnValue,
    launcherFn,
    gridKey,
}: PlAgGridProps) {
    const gridRef = useRef<AgGridReact>(); // for accessing Grid's API
    const modalContext = useModal();

    //Hook the OData provider up to the grid
    const onGridReady = (params: any) => {
        const gridApi = params.api;
        gridApi.setServerSideDatasource(
            new OdataProvider({
                callApi: (args: ApiCallArgs) =>
                    makeCall({...args.options}).unwrap(),
            })
        );
    };

    //Refresh grid contents after updates to the server-side model
    const refreshGrid = () => {
        gridRef.current.api.refreshServerSide();
    };

    //Handler for the Clear Filters button - does what's advertised
    const clearFilters = () => {
        gridRef.current.api.setFilterModel(null);
    };

    //Handler for the Add Row button - triggers a popup to fill in the needed fields
    const tryAdd = () => {
        inputFieldsForm(
            modalContext,
            addFormHeader,
            addFormFields,
            (values) => {
                addRow(values);
                modalContext.closeModal();
            },
            'Add'
        );
    };
    //"Add Row" API call
    const addRow = (input) => {
        handleLoading(
            gridRef,
            createMutation(getCreateMutationObject(input)).then((response) => {
                addHandler(response);
            })
        );
    };
    //Handle showing success/failure
    const addHandler = (response) => {
        if (response.error) {
            addError(getErrorText(response.error.data), 'add');
        } else {
            showToastAlert(
                'Success',
                addToastSuccessMessage,
                'success',
                modalContext.showModal,
                modalContext.closeModal
            );
            if (onMutationSuccess) {
                onMutationSuccess();
            }
        }
        refreshGrid();
    };
    //Error dialog for failed adds
    const addError = (errorDetails, addType) => {
        okOnlyForm(
            modalContext,
            'Unable to ' + addType + ' requested record',
            errorDetails,
            () => {
                modalContext.closeModal();
            }
        );
    };

    //Handler for the Custom button - triggers a popup to fill in the needed fields
    const tryCustom = () => {
        inputFieldsForm(
            modalContext,
            customFormHeader,
            customFormFields,
            (values) => {
                customRow(values);
                modalContext.closeModal();
            },
            customButtonConfirmText
        );
    };
    //"Custom Row" API call
    const customRow = (input) => {
        handleLoading(
            gridRef,
            customCreateMutation(getCustomCreateMutationObject(input)).then((response) => {
                customHandler(response);
            })
        );
    };
    //Handle showing custom success/failure
    const customHandler = (response) => {
        if (response.error) {
            addError(getErrorText(response.error.data), customButtonText);
        } else {
            showToastAlert(
                'Success',
                customToastSuccessMessage,
                'success',
                modalContext.showModal,
                modalContext.closeModal
            );
            // Call the custom success callback if provided
            if (customSuccessCallback) {
                customSuccessCallback();
            }
        }
        refreshGrid();
    };

    //Handle updating a row's data
    const updateCell = (event) => {
        if (event.newValue === undefined) {
            okOnlyForm(
                modalContext,
                "The value used for '" +
                    event.colDef.headerName +
                    "' is " +
                    getFieldLimits(event.colDef),
                'The previous value will be restored',
                () => {
                    modalContext.closeModal();
                }
            );
            return;
        }

        //If a column has been designated the create/delete checkbox column, it may override here
        if (event.colDef.field === selectionColumnKey) {
            if (event.newValue === selectionColumnValue) {
                addRow(event);
            } else {
                doDelete(event);
            }
            return;
        }

        const data = getPutMutationObject(event);
        if (!data) {
            return;
        }

        handleLoading(
            gridRef,
            putMutation(data).then((response) => updateHandler(response))
        );
    };
    //Show an error if update failed, then refresh grid either way
    const updateHandler = (response) => {
        if (response.error) {
            updateError(getErrorText(response.error.data));
        } else {
            if (onMutationSuccess) {
                onMutationSuccess();
            }
        }
        refreshGrid();
    };
    //Error dialog for failed updates
    const updateError = (errorDetails) => {
        okOnlyForm(
            modalContext,
            'Unable to update requested record',
            errorDetails,
            () => {
                modalContext.closeModal();
            }
        );
    };

    //Handler for the delete column's click event
    const deleteCellClicked = (params) => {
        tryDelete(params);
    };
    //Popup confirmation for deleting a row
    const tryDelete = (params) => {
        okCancelForm(
            modalContext,
            'Delete',
            getDeleteMessage(params),
            'Delete',
            () => {
                doDelete(params);
                modalContext.closeModal();
            }
        );
    };
    //"Delete Row" API Call
    const doDelete = (params) => {
        handleLoading(
            gridRef,
            deleteMutation(getDeleteMutationObject(params)).then((response) =>
                deleteHandler(response)
            )
        );
    };
    //Handle showing delete success/failure
    const deleteHandler = (response) => {
        if (response.error) {
            deleteError(getErrorText(response.error.data));
        } else {
            if (deleteToastSuccessMessage) {
                showToastAlert(
                    'Success',
                    deleteToastSuccessMessage,
                    'success',
                    modalContext.showModal,
                    modalContext.closeModal
                );
            }
            if (onMutationSuccess) {
                onMutationSuccess();
            }
        }
        refreshGrid();
    };
    //Error dialog for failed deletes
    const deleteError = (errorDetails) => {
        okOnlyForm(
            modalContext,
            'Unable to delete requested record',
            errorDetails,
            () => {
                modalContext.closeModal();
            }
        );
    };

    //Handler for the Copy button - triggers a popup to fill in the needed fields
    const tryCopy = (params) => {
        const copyFields = copyFormFields.map((field) => {
            if (field.key !== 'copyId') {
                return field;
            } else {
                return {
                    ...field,
                    initVal: {
                        id: params.data[field.valueQueryIdIndex],
                        label: params.data[field.valueQueryNameIndex],
                    },
                };
            }
        });

        inputFieldsForm(
            modalContext,
            copyFormHeader,
            copyFields,
            (values) => {
                copyRow(values, params.data);
                modalContext.closeModal();
            },
            'Copy'
        );
    };
    //"Copy Row" API call
    const copyRow = (input, data) => {
        handleLoading(
            gridRef,
            copyMutation(getCopyMutationObject(input, data)).then(
                (response) => {
                    copyHandler(response);
                }
            )
        );
    };
    //Handle showing copy success/failure
    const copyHandler = (response) => {
        if (response.error) {
            addError(getErrorText(response.error.data), 'copy');
        } else {
            showToastAlert(
                'Success',
                copyToastSuccessMessage,
                'success',
                modalContext.showModal,
                modalContext.closeModal
            );
        }
        refreshGrid();
    };

    //Augment provided simple columns with the fancier Delete column (with internal hooks)
    const getColumnDefs = () => {
        const delCol = {
            field: 'delete',
            ...(wideGrid && {width: 100}),
            ...(!wideGrid && {flex: 1}),
            filter: false,
            floatingFilter: false,
            sortable: false,
            editable: false,
            suppressMenu: true,
            onCellClicked: deleteCellClicked,
            cellRenderer: DeleteIconRenderer,
        };
        const copyCol = {
            field: 'copy',
            ...(wideGrid && {width: 100}),
            ...(!wideGrid && {flex: 1}),
            filter: false,
            floatingFilter: false,
            sortable: false,
            editable: false,
            suppressMenu: true,
            onCellClicked: tryCopy,
            cellRenderer: CopyIconRenderer,
        };

        return [
            ...columns,
            ...(canCopy ? [copyCol] : []),
            ...(canDelete ? [delCol] : []),
        ];
    };
    const [columnDefs, setColumnDefs] = useState(getColumnDefs());

    //Check delete/edit security
    const checkSecurity = () => {
        const columnDefs = getColumnDefs();
        if (gridRef && gridRef.current && gridRef.current.columnApi) {
            if (!canDelete) {
                gridRef.current.columnApi.setColumnVisible('delete', false);
            }
            if (!canEdit || !!launcherFn) {
                const lockedCols = columnDefs.map((element) => {
                    return element.field === 'delete'
                        ? {
                              ...element,
                          }
                        : {
                              ...element,
                              editable: false,
                              onCellClicked: launchHandler,
                          };
                });

                setColumnDefs(lockedCols);
            }
        }
    };

    //Wrapper to send row data to the external launch function
    const launchHandler = (event: CellClickedEvent) => {
        if (launcherFn) {
            launcherFn(event.data);
        }
    };

    //Function for assigning row IDs
    const rowIdMemo = useMemo(() => {
        return getRowId ? (params) => getRowId(params) : null;
    }, [getRowId]);

    //Render the container, grid, and add button
    return (
        <Container
            sx={wideGrid ? {height: '54vh'} : {height: '60vh'}}
            className="ag-theme-material">
            {!addButtonFunc ? (
                <></>
            ) : (
                <CanI
                    functionId={addButtonFunc}
                    action={addButtonAction}
                    alsoRequire={addButtonExtraFunc}>
                    <Button id="btnAdd" onClick={tryAdd}>
                        Add Row
                    </Button>
                </CanI>
            )}
            {!customButtonFunc ? (
                <></>
            ) : (
                <CanI
                    functionId={customButtonFunc}
                    action={customButtonAction}
                    alsoRequire={customButtonExtraFunc}>
                    <Button
                        id="btnCustom"
                        onClick={customButtonOnClick || tryCustom}
                        style={{marginLeft: '8px'}}>
                        {customButtonText || 'Custom'}
                    </Button>
                </CanI>
            )}
            <Button
                id="btnClearFilters"
                onClick={clearFilters}
                style={{marginLeft: '8px'}}>
                Clear Filters
            </Button>
            <div style={{height: '90%'}}>
                <AgGridReact
                    ref={gridRef}
                    key={gridKey}
                    //Enable server mode DataSource
                    rowModelType="serverSide"
                    // fetch 100 rows per at a time
                    cacheBlockSize={100}
                    onGridReady={onGridReady}
                    rowGroupPanelShow="onlyWhenGrouping"
                    pivotPanelShow="always"
                    suppressDragLeaveHidesColumns
                    suppressMakeColumnVisibleAfterUnGroup
                    maintainColumnOrder
                    columnDefs={columnDefs}
                    defaultColDef={{
                        sortable: true,
                        resizable: true,
                        editable: true,
                        enablePivot: true,
                        allowedAggFuncs: ['sum', 'min', 'max', 'avg', 'count'],
                        filterParams: {
                            newRowsAction: 'keep',
                            browserDatePicker: true,
                            suppressSelectAll: true,
                            defaultToNothingSelected: true,
                        },
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                    }}
                    readOnlyEdit={true} // Edits are processed as requests so we can react to them better
                    tooltipShowDelay={100}
                    tooltipHideDelay={10000}
                    onCellEditRequest={updateCell} // Hook that's called after requesting an edit
                    onFirstDataRendered={checkSecurity} // Post-render callback for first-time setup
                    getRowId={rowIdMemo} // ID assignment logic
                />
            </div>
        </Container>
    );
});

export default MemoizedPlAgGrid;
